import { API, graphqlOperation } from 'aws-amplify';
import { createAccountOpeningBalancesTransaction } from '../graphql';
import { showErrorSnackbar, showSuccessSnackbar } from '../components/snackBar/snakbar';

/**
 * Save opening balance with provided account data
 * @param {Array} accountData - Array of account opening balance data
 * @param {number} entryDate - Entry date in unix timestamp
 * @param {string} action - Action type ('save', 'edit', etc.)
 * @returns {Promise<Object>} - Response from the mutation
 */
export const saveOpeningBalance = async (accountData, entryDate, action = 'save') => {
  try {
    const opbTransaction = {
      bankreferencenumber: '',
      comments: '',
      entrydate: '',
      transactiontypecd: 'OpeningBalance',
      vouchercode: 0,
    };

    const total = accountData
      .filter((account) => account?.accountid !== 12)
      .reduce(
        (acc, item) => {
          acc.creditAmount += item.creditamount || 0;
          acc.debitAmount += item.debitamount || 0;
          return acc;
        },
        { creditAmount: 0, debitAmount: 0 }
      );

    const debitCreditEntry = {
      accountid: 12,
      comments: '',
      startdate: '',
      creditamount: total?.debitAmount > total?.creditAmount ? total?.debitAmount - total?.creditAmount : 0,
      debitamount: total?.creditAmount > total?.debitAmount ? total?.creditAmount - total?.debitAmount : 0,
      transactiontypecd: 'OpeningBalance',
      companybranchid: 0,
    };

    const allAccounts = accountData
      .filter((account) => account.accountid !== 12)
      .map((account) => ({
        accountid: account?.accountid,
        comments: '',
        creditamount: parseFloat(account?.creditamount) || 0,
        debitamount: parseFloat(account?.debitamount) || 0,
        financialyearid: 0,
        startdate: '',
        transactiontypecd: 'OpeningBalance',
        companybranchid: 0,
      }));

    const masterData = [debitCreditEntry, ...allAccounts];

    const transactionMasterJson = JSON.stringify([opbTransaction]);
    const transactionDetailsJson = JSON.stringify(masterData);

    const response = await API.graphql(
      graphqlOperation(createAccountOpeningBalancesTransaction, {
        createAccountOpeningBalancesArrayInput: {
          openingbalancedate: entryDate,
          transactionmaster: transactionMasterJson,
          transactiondetails: transactionDetailsJson,
        },
      })
    );

    const result = response?.data?.createAccountOpeningBalancesTransaction;

    if (result?.statuscode === 'Success') {
      if (action && action !== 'edit') {
        showSuccessSnackbar(result.statusmessage);
      }
      return result;
    } else {
      throw new Error(result?.statusmessage || 'Failed to save opening balance');
    }
  } catch (error) {
    if (error?.errors?.[0]?.message?.includes('date constraints')) {
      const errorMessage = `Transactions already exist before the selected date. Please change the Opening Balance date.`;
      showErrorSnackbar(errorMessage);
    } else if (error?.errors?.[0]?.message?.includes('Transactions already exist')) {
      showErrorSnackbar('Transactions already exist before the Opening Balance Date');
    } else {
      showErrorSnackbar('An unexpected error occurred while saving details. Please try again later.');
    }
    throw error;
  }
};
